import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  Grid,
  Stack,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  Tabs,
  Tab,
  Avatar
} from "@mui/material";
import { getHttpClient } from "src/services/rest-api";
import { HTTPService } from "src/services/httpservice";
import AndroidIcon from "@mui/icons-material/Android";
import AppleIcon from "@mui/icons-material/Apple";
import HistoryIcon from "@mui/icons-material/History";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";

interface AppRelease {
  id: number;
  platform: string;
  latest_version: string;
  min_version: string;
  update_url: string;
  title: string;
  message: string;
  force_update: boolean;
  is_active: boolean;
  created_by: string;
  createdAt: string;
}

const AppSettingsLayout = () => {
  const [history, setHistory] = useState<AppRelease[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<{ [key: string]: boolean }>({ android: false, ios: false });
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<number>(0); // 0 = Android, 1 = iOS

  // Form states per platform
  const [forms, setForms] = useState<{
    [key: string]: {
      latest_version: string;
      min_version: string;
      update_url: string;
      title: string;
      message: string;
      force_update: boolean;
    };
  }>({
    android: { latest_version: "", min_version: "", update_url: "", title: "", message: "", force_update: false },
    ios: { latest_version: "", min_version: "", update_url: "", title: "", message: "", force_update: false },
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getHttpClient(HTTPService.GET_RELEASE_HISTORY, "GET", null, null, null);
      const historyData: AppRelease[] = res.data;
      setHistory(historyData);

      // Extract active releases to populate the form fields
      const activeAndroid = historyData.find((r) => r.platform === "android" && r.is_active);
      const activeIos = historyData.find((r) => r.platform === "ios" && r.is_active);

      setForms({
        android: {
          latest_version: activeAndroid?.latest_version || "1.0.0",
          min_version: activeAndroid?.min_version || "1.0.0",
          update_url: activeAndroid?.update_url || "",
          title: activeAndroid?.title || "New Version Available",
          message: activeAndroid?.message || "",
          force_update: activeAndroid?.force_update || false,
        },
        ios: {
          latest_version: activeIos?.latest_version || "1.0.0",
          min_version: activeIos?.min_version || "1.0.0",
          update_url: activeIos?.update_url || "",
          title: activeIos?.title || "New Version Available",
          message: activeIos?.message || "",
          force_update: activeIos?.force_update || false,
        },
      });
    } catch (err: any) {
      console.error(err);
      setError(err?.message || "Failed to load release history settings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleInputChange = (platform: "android" | "ios", field: string, value: any) => {
    setForms((prev) => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        [field]: value,
      },
    }));
  };

  const handleSave = async (platform: "android" | "ios") => {
    const formData = forms[platform];

    if (!formData.latest_version || !formData.min_version || !formData.update_url) {
      setError("Please fill out all required fields (Latest Version, Min Version, and Store URL).");
      return;
    }

    try {
      setSaving((prev) => ({ ...prev, [platform]: true }));
      await getHttpClient(
        HTTPService.CREATE_RELEASE,
        "POST",
        {
          platform,
          ...formData,
        },
        null,
        null
      );
      setSuccessMsg(`Successfully saved and activated new ${platform.toUpperCase()} release.`);
      fetchData(); // Reload history
    } catch (err: any) {
      console.error(err);
      setError(err?.message || `Failed to save ${platform.toUpperCase()} configuration.`);
    } finally {
      setSaving((prev) => ({ ...prev, [platform]: false }));
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  const activeAndroid = history.find((r) => r.platform === "android" && r.is_active);
  const activeIos = history.find((r) => r.platform === "ios" && r.is_active);

  const currentPlatform = activeTab === 0 ? "android" : "ios";
  const form = forms[currentPlatform];
  const isSaving = saving[currentPlatform];

  return (
    <Stack spacing={4} sx={{ maxWidth: 1000, margin: "0 auto", pb: 6 }}>
      {/* HEADER SECTION */}
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Box>
          <Typography fontSize="24px" fontWeight={800} color="text.primary" letterSpacing="-0.5px">
            App Release Control
          </Typography>
          <Typography fontSize="14px" color="text.secondary">
            Manage target version rules, release notes, and track chronological deployment audit logs.
          </Typography>
        </Box>
      </Box>

      {/* QUICK STATS CARDS */}
      <Grid container spacing={3}>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined" sx={{ p: 2.5, borderRadius: "16px", backgroundColor: "#fff" }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ bgcolor: "#E8F5E9", color: "#2E7D32" }}>
                <AndroidIcon />
              </Avatar>
              <Box>
                <Typography fontSize="12px" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase" }}>
                  Android Live
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography fontSize="18px" fontWeight={700}>
                    {activeAndroid?.latest_version || "—"}
                  </Typography>
                  {activeAndroid?.is_active && (
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#4caf50", boxShadow: "0 0 8px #4caf50" }} />
                  )}
                </Box>
              </Box>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card variant="outlined" sx={{ p: 2.5, borderRadius: "16px", backgroundColor: "#fff" }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ bgcolor: "#F5F5F7", color: "#1C1C1E" }}>
                <AppleIcon />
              </Avatar>
              <Box>
                <Typography fontSize="12px" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase" }}>
                  iOS Live
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography fontSize="18px" fontWeight={700}>
                    {activeIos?.latest_version || "—"}
                  </Typography>
                  {activeIos?.is_active && (
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#4caf50", boxShadow: "0 0 8px #4caf50" }} />
                  )}
                </Box>
              </Box>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card variant="outlined" sx={{ p: 2.5, borderRadius: "16px", backgroundColor: "#fff" }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ bgcolor: "#E3F2FD", color: "#1976D2" }}>
                <HistoryIcon />
              </Avatar>
              <Box>
                <Typography fontSize="12px" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase" }}>
                  Total Releases
                </Typography>
                <Typography fontSize="18px" fontWeight={700}>
                  {history.length} audit entries
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* PLATFORM MANAGER CARD */}
      <Card
        variant="outlined"
        sx={{
          borderRadius: "20px",
          borderColor: "divider",
          backgroundColor: "#fff",
          boxShadow: "0 8px 32px rgba(0,0,0,0.03)",
          overflow: "hidden",
        }}
      >
        {/* Navigation Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "#f8fafc" }}>
          <Tabs
            value={activeTab}
            onChange={(_, val) => setActiveTab(val)}
            indicatorColor="primary"
            textColor="primary"
            sx={{ px: 2 }}
          >
            <Tab
              icon={<AndroidIcon sx={{ fontSize: "20px" }} />}
              iconPosition="start"
              label="Android Release Configuration"
              sx={{ py: 2, textTransform: "none", fontWeight: 700 }}
            />
            <Tab
              icon={<AppleIcon sx={{ fontSize: "20px" }} />}
              iconPosition="start"
              label="iOS Release Configuration"
              sx={{ py: 2, textTransform: "none", fontWeight: 700 }}
            />
          </Tabs>
        </Box>

        <Box sx={{ p: 4 }}>
          {/* Version Configuration Form */}
          <Stack spacing={4}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Stack spacing={3}>
                  <Typography fontSize="14px" fontWeight={700} color="text.primary">
                    Deployment Scope
                  </Typography>

                  <Box display="flex" gap={2}>
                    <TextField
                      fullWidth
                      sx={{ flex: 1 }}
                      label="Latest Release Version"
                      placeholder="e.g. 1.0.3"
                      variant="outlined"
                      value={form.latest_version}
                      onChange={(e) => handleInputChange(currentPlatform, "latest_version", e.target.value)}
                      helperText="The target version published in the app store"
                    />
                    <TextField
                      fullWidth
                      sx={{ flex: 1 }}
                      label="Minimum Supported Version"
                      placeholder="e.g. 1.0.1"
                      variant="outlined"
                      value={form.min_version}
                      onChange={(e) => handleInputChange(currentPlatform, "min_version", e.target.value)}
                      helperText="Versions below this will be forced to update"
                    />
                  </Box>

                  <TextField
                    fullWidth
                    label="Store Download Redirect Link"
                    placeholder="https://play.google.com/... or https://apps.apple.com/..."
                    variant="outlined"
                    value={form.update_url}
                    onChange={(e) => handleInputChange(currentPlatform, "update_url", e.target.value)}
                    helperText="URL launched when users click 'Update Now'"
                  />
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Stack spacing={3}>
                  <Typography fontSize="14px" fontWeight={700} color="text.primary">
                    Update Notice Details
                  </Typography>

                  <TextField
                    fullWidth
                    label="Popup Title"
                    placeholder="e.g. Recommended Update Available"
                    variant="outlined"
                    value={form.title}
                    onChange={(e) => handleInputChange(currentPlatform, "title", e.target.value)}
                  />

                  <TextField
                    fullWidth
                    label="What's New (Release Notes)"
                    placeholder="Describe modifications (e.g. • Faster sync\n• Performance optimizations)"
                    multiline
                    rows={4}
                    variant="outlined"
                    value={form.message}
                    onChange={(e) => handleInputChange(currentPlatform, "message", e.target.value)}
                  />
                </Stack>
              </Grid>
            </Grid>

            {/* Emergency Alerts if toggle on */}
            {form.force_update ? (
              <Alert
                severity="error"
                icon={<ErrorIcon fontSize="inherit" />}
                sx={{ borderRadius: "12px", border: "1px solid #fee2e2", backgroundColor: "#fef2f2" }}
              >
                <Typography fontSize="13px" fontWeight={700} color="error.dark">
                  EMERGENCY FORCE MODE ACTIVE
                </Typography>
                <Typography fontSize="12px" color="error.dark">
                  This override will immediately lock the app and force all users running any version older than <strong>{form.latest_version}</strong> to update before continuing.
                </Typography>
              </Alert>
            ) : (
              <Alert
                severity="info"
                icon={<CheckCircleIcon fontSize="inherit" />}
                sx={{ borderRadius: "12px", border: "1px solid #e0f2fe", backgroundColor: "#f0f9ff" }}
              >
                <Typography fontSize="12px" color="primary.dark">
                  Users running versions between <strong>{form.min_version}</strong> and <strong>{form.latest_version}</strong> will see a recommended update prompt which they can dismiss. Only users below <strong>{form.min_version}</strong> are blocked.
                </Typography>
              </Alert>
            )}

            <Divider />

            {/* Submit Bar */}
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleSave(currentPlatform)}
                disabled={isSaving}
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 700,
                  px: 5,
                  height: 46,
                  boxShadow: "0 4px 12px rgba(44,100,227,0.2)",
                }}
              >
                {isSaving ? "Saving Release..." : `Deploy ${currentPlatform === "android" ? "Android" : "iOS"} Config`}
              </Button>
            </Box>
          </Stack>
        </Box>
      </Card>

      {/* HISTORICAL AUDIT LOGS */}
      <Card
        variant="outlined"
        sx={{
          p: 4,
          borderRadius: "20px",
          borderColor: "divider",
          backgroundColor: "#fff",
          boxShadow: "0 8px 32px rgba(0,0,0,0.03)",
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5} mb={3}>
          <Avatar sx={{ bgcolor: "#ECEFF1", color: "#455A64", width: 36, height: 36 }}>
            <HistoryIcon sx={{ fontSize: "20px" }} />
          </Avatar>
          <Box>
            <Typography fontSize="16px" fontWeight={700}>
              Deployments Audit Trail
            </Typography>
            <Typography fontSize="12px" color="text.secondary">
              Chronological ledger of release updates configured by administrators
            </Typography>
          </Box>
        </Box>

        {history.length === 0 ? (
          <Box py={4} textAlign="center">
            <Typography color="text.secondary" fontSize="13px">
              No historical releases recorded yet.
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} elevation={0} variant="outlined" sx={{ borderRadius: "12px", border: "1px solid #e2e8f0" }}>
            <Table size="medium">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                  <TableCell sx={{ fontWeight: 700, color: "text.secondary", fontSize: "11px", textTransform: "uppercase" }}>Timestamp</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: "text.secondary", fontSize: "11px", textTransform: "uppercase" }}>Platform</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: "text.secondary", fontSize: "11px", textTransform: "uppercase" }}>Latest Version</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: "text.secondary", fontSize: "11px", textTransform: "uppercase" }}>Min Version</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: "text.secondary", fontSize: "11px", textTransform: "uppercase" }}>Emergency Mode</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: "text.secondary", fontSize: "11px", textTransform: "uppercase" }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: "text.secondary", fontSize: "11px", textTransform: "uppercase" }}>Configured By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((row) => (
                  <TableRow key={row.id} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                    <TableCell sx={{ fontSize: "12px", py: 2 }}>
                      {new Date(row.createdAt).toLocaleDateString()} {new Date(row.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Chip
                        label={row.platform.toUpperCase()}
                        size="small"
                        sx={{
                          fontWeight: 800,
                          fontSize: "10px",
                          backgroundColor: row.platform === "android" ? "#E8F5E9" : "#ECEFF1",
                          color: row.platform === "android" ? "#1B5E20" : "#263238",
                          borderRadius: "6px"
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: "12px", fontWeight: 700, py: 2 }}>{row.latest_version}</TableCell>
                    <TableCell sx={{ fontSize: "12px", py: 2 }}>{row.min_version}</TableCell>
                    <TableCell sx={{ py: 2 }}>
                      {row.force_update ? (
                        <Chip label="Emergency Active" color="error" size="small" sx={{ fontWeight: 600, fontSize: "10px", borderRadius: "6px" }} />
                      ) : (
                        <Typography fontSize="12px" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      {row.is_active ? (
                        <Chip label="Live Active" color="success" variant="outlined" size="small" sx={{ fontWeight: 800, fontSize: "10px", borderRadius: "6px" }} />
                      ) : (
                        <Chip label="Archived" size="small" variant="outlined" sx={{ fontSize: "10px", color: "text.disabled", borderColor: "divider", borderRadius: "6px" }} />
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: "12px", py: 2, color: "text.secondary" }}>{row.created_by}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* NOTIFICATIONS */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: "100%", borderRadius: "8px" }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!successMsg}
        autoHideDuration={4000}
        onClose={() => setSuccessMsg(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={() => setSuccessMsg(null)} severity="success" sx={{ width: "100%", borderRadius: "8px" }}>
          {successMsg}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

export default AppSettingsLayout;
